import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { socket } from './socket';
import { audio } from './audio';
import { saveSession, clearSession, getSavedSession, getSessionToResume, clearDrawingDraft } from './session';
import Home from './components/Home';
import Lobby from './components/Lobby';
import PromptEntry from './components/PromptEntry';
import DrawingCanvas from './components/DrawingCanvas';
import GuessDrawing from './components/GuessDrawing';
import Results from './components/Results';
import GameCountdown from './components/GameCountdown';

function App() {
  const [isConnected, setIsConnected] = useState(false);
  // ─── Dev shortcut: ?screen=drawing&prompt=... skips to drawing UI ───
  const _devParams = process.env.NODE_ENV === 'development' ? new URLSearchParams(window.location.search) : null;
  const _devScreen = _devParams?.get('screen') || null;

  const [currentScreen, setCurrentScreen] = useState(_devScreen || 'home'); // home, lobby, prompt_entry, drawing, guessing, results
  const [roomCode, setRoomCode] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [playerId, setPlayerId] = useState('');
  const [players, setPlayers] = useState([]);
  const [submittedCount, setSubmittedCount] = useState(0);
  const [submittedPlayers, setSubmittedPlayers] = useState(new Set());
  const [assignment, setAssignment] = useState(
    _devScreen === 'drawing'
      ? { prompt: _devParams?.get('prompt') || 'a cat riding a bicycle' }
      : null
  );
  const [roundSubmittedCount, setRoundSubmittedCount] = useState(0);
  const [roundSubmittedPlayers, setRoundSubmittedPlayers] = useState(new Set());
  const [roundTimeLeft, setRoundTimeLeft] = useState(120);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [submittedPrompt, setSubmittedPrompt] = useState('');
  const [isHostOverride, setIsHostOverride] = useState(null); // set from server on rejoin
  const [currentRound, setCurrentRound] = useState(0);
  const [totalRounds, setTotalRounds] = useState(0);
  const [chainsData, setChainsData] = useState([]);
  const [resultsChainIndex, setResultsChainIndex] = useState(-1);
  const [resultsStepSignal, setResultsStepSignal] = useState(null);
  const [resultsInitialChainIndex, setResultsInitialChainIndex] = useState(null);
  const [resultsInitialStepIndex, setResultsInitialStepIndex] = useState(null);
  const [reactionSignal, setReactionSignal] = useState(null);
  const [settings, setSettings] = useState({
    drawing_timer: 120,
    guess_timer: 45,
    chain_length: 'normal',
  });
  const [promptSuggestions, setPromptSuggestions] = useState([]);
  const [lobbyStrokes, setLobbyStrokes] = useState([]);
  const [disconnectedPlayerIds, setDisconnectedPlayerIds] = useState(new Set());
  const [unresponsivePlayerIds, setUnresponsivePlayerIds] = useState(new Set());
  const pendingGameDataRef = useRef(null);

  // Set while a mount-time rejoin is in flight, so a failure (room expired,
  // player gone) clears the stale session silently instead of showing an error.
  const resumePendingRef = useRef(false);

  // ─── Audio: whoosh on phase transitions (skip countdown — it has its own vibe) ───
  const prevScreenRef = useRef(null);
  useEffect(() => {
    if (prevScreenRef.current !== null && prevScreenRef.current !== currentScreen && currentScreen !== 'countdown') {
      audio.whoosh();
    }
    prevScreenRef.current = currentScreen;
  }, [currentScreen]);

  // Keep a ref so the connect handler can read currentScreen without going stale
  const currentScreenRef = useRef(currentScreen);
  currentScreenRef.current = currentScreen;

  // ─── Invite link: extract ?join=ROOMCODE from URL ───
  const [joinError, setJoinError] = useState(''); // surfaced on the home/join screen
  const [submissionError, setSubmissionError] = useState(''); // moderation rejection during prompt/guess
  const [inviteRoomCode, setInviteRoomCode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('join')?.toUpperCase() || '';
  });

  // Clear the ?join= param from the URL after reading it (keeps URL clean)
  useEffect(() => {
    if (inviteRoomCode) {
      const url = new URL(window.location);
      url.searchParams.delete('join');
      window.history.replaceState({}, '', url.toString());
    }
  }, [inviteRoomCode]);

  // ─── Debug mode: skip to a screen via ?debug=drawing or ?debug=results ───
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const debugScreen = params.get('debug');
    if (!debugScreen) return;

    // Generate a sample drawing for testing
    function makeSampleDrawing() {
      const strokes = [];
      let t = 0;
      // Circle
      const circle = [];
      for (let a = 0; a <= Math.PI * 2; a += 0.1) {
        circle.push({ x: 400 + Math.cos(a) * 120, y: 280 + Math.sin(a) * 120, t });
        t += 20;
      }
      strokes.push({ id: 's1', type: 'stroke', tool: 'brush', color: '#000000', size: 4, points: circle, startTime: circle[0].t, endTime: circle[circle.length - 1].t });
      t += 100;
      // Left eye
      const le = [];
      for (let a = 0; a <= Math.PI * 2; a += 0.2) { le.push({ x: 360 + Math.cos(a) * 15, y: 250 + Math.sin(a) * 15, t }); t += 15; }
      strokes.push({ id: 's2', type: 'stroke', tool: 'brush', color: '#000000', size: 4, points: le, startTime: le[0].t, endTime: le[le.length - 1].t });
      t += 100;
      // Right eye
      const re = [];
      for (let a = 0; a <= Math.PI * 2; a += 0.2) { re.push({ x: 440 + Math.cos(a) * 15, y: 250 + Math.sin(a) * 15, t }); t += 15; }
      strokes.push({ id: 's3', type: 'stroke', tool: 'brush', color: '#000000', size: 4, points: re, startTime: re[0].t, endTime: re[re.length - 1].t });
      t += 100;
      // Smile
      const sm = [];
      for (let a = 0.3; a <= Math.PI - 0.3; a += 0.1) { sm.push({ x: 400 + Math.cos(a) * 70, y: 300 + Math.sin(a) * 50, t }); t += 20; }
      strokes.push({ id: 's4', type: 'stroke', tool: 'brush', color: '#e91e63', size: 5, points: sm, startTime: sm[0].t, endTime: sm[sm.length - 1].t });
      return { canvas: { width: 800, height: 600, background: '#ffffff' }, strokes, totalDuration: t };
    }

    setPlayerId('debug-player');
    setPlayerName('Debug User');
    setRoomCode('DEBUG');

    if (debugScreen === 'drawing') {
      setAssignment({ text: 'A cat riding a skateboard', author: 'TestPlayer' });
      setPlayers([{ id: 'debug-player', name: 'Debug User' }]);
      setCurrentScreen('drawing');
    } else if (debugScreen === 'guessing') {
      setAssignment({ drawing_data: makeSampleDrawing(), artist: 'TestPlayer' });
      setPlayers([{ id: 'debug-player', name: 'Debug User' }]);
      setCurrentScreen('guessing');
    } else if (debugScreen === 'results') {
      setIsHostOverride(true); // debug user acts as host
      setChainsData([
        {
          chain_index: 0,
          original_author: 'Alice',
          original_author_avatar: 'm0',
          steps: [
            { type: 'prompt', player_id: 'p1', player_name: 'Alice', player_avatar: 'm0', text: 'A happy sun' },
            { type: 'drawing', player_id: 'p2', player_name: 'Bob', player_avatar: 'm1', drawing_data: makeSampleDrawing() },
            { type: 'guess', player_id: 'p3', player_name: 'Charlie', player_avatar: 'm2', text: 'A smiley face' },
          ],
        },
        {
          chain_index: 1,
          original_author: 'Bob',
          original_author_avatar: 'm1',
          steps: [
            { type: 'prompt', player_id: 'p2', player_name: 'Bob', player_avatar: 'm1', text: 'Dancing penguin' },
            { type: 'drawing', player_id: 'p3', player_name: 'Charlie', player_avatar: 'm2', drawing_data: makeSampleDrawing() },
            { type: 'guess', player_id: 'p1', player_name: 'Alice', player_avatar: 'm0', text: 'A bird doing a jig' },
          ],
        },
      ]);
      setCurrentScreen('results');
    }
  }, []);

  // ─── One-time session resume on mount ───
  // The socket connects immediately (autoConnect:true) before any useEffect
  // runs, so we can't rely on the 'connect' event. Instead, check on mount:
  // if already connected, emit now; otherwise wait for the first connect.
  useEffect(() => {
    const attemptRejoin = () => {
      const saved = getSessionToResume();
      if (!saved || currentScreenRef.current !== 'home') return;
      console.log('[Session] Attempting rejoin for', saved.playerName, 'in room', saved.roomCode);
      resumePendingRef.current = true;
      socket.emit('rejoin_room', { room_code: saved.roomCode, player_name: saved.playerName });
    };
    if (socket.connected) {
      attemptRejoin();
    } else {
      socket.once('connect', attemptRejoin);
    }
    return () => socket.off('connect', attemptRejoin);
  }, []);

  useEffect(() => {
    function onConnect() {
      setIsConnected(true);
      console.log('Connected to server');
      // On reconnect (WiFi drop/resume, brief disconnect), re-subscribe to the
      // room so the server routes events to the new socket ID. Skip on initial
      // connect (screen is still 'home') to avoid racing with the mount-time
      // rejoin in the useEffect([]) above.
      const screen = currentScreenRef.current;
      if (screen !== 'home' && screen !== 'thanks') {
        const saved = getSavedSession();
        if (saved) {
          console.log('[Session] Reconnected mid-game — rejoining room', saved.roomCode);
          socket.emit('rejoin_room', { room_code: saved.roomCode, player_name: saved.playerName });
        }
      }
    }

    function onDisconnect() {
      setIsConnected(false);
      console.log('Disconnected from server');
    }

    function onConnected(data) {
      setPlayerId(data.sid);
    }

    function onRoomCreated(data) {
      setRoomCode(data.room_code);
      setPlayerName(data.player_name);
      setPlayers([{ id: data.player_id, name: data.player_name, avatar: data.avatar }]);
      setIsHostOverride(true); // creator is always the host
      saveSession(data.room_code, data.player_name);
      setCurrentScreen('lobby');
    }

    function onRoomJoined(data) {
      setRoomCode(data.room_code);
      setPlayerName(data.player_name);
      setPlayers(data.players);
      setIsHostOverride(false); // joiner is never the host
      if (data.settings) setSettings(data.settings);
      if (data.lobby_strokes) setLobbyStrokes(data.lobby_strokes);
      saveSession(data.room_code, data.player_name);
      setCurrentScreen('lobby');
    }

    function onRoomRejoined(data) {
      resumePendingRef.current = false;
      console.log('[Session] Rejoined room', data.room_code, 'phase=', data.phase);
      setRoomCode(data.room_code);
      setPlayerName(data.player_name);
      setPlayerId(data.player_id);
      setPlayers(data.players);
      setIsHostOverride(data.is_host);
      if (data.settings) setSettings(data.settings);
      if (data.lobby_strokes) setLobbyStrokes(data.lobby_strokes);
      const phase = data.phase;
      if (phase === 'waiting') {
        setCurrentScreen('lobby');
      } else if (phase === 'prompt_entry') {
        setRoundTimeLeft(data.timeout ?? 60);
        setAlreadySubmitted(data.already_submitted || false);
        setSubmittedPrompt(data.submitted_prompt || '');
        const ids = data.submitted_player_ids || [];
        setSubmittedCount(ids.length);
        setSubmittedPlayers(new Set(ids));
        setCurrentScreen('prompt_entry');
      } else if (phase === 'drawing' || phase === 'guessing') {
        setCurrentRound(data.round ?? 0);
        setTotalRounds(data.total_rounds ?? 0);
        setRoundTimeLeft(data.timeout ?? 90);
        setAlreadySubmitted(data.already_submitted || false);
        if (data.assignment) setAssignment(data.assignment);
        const ids = data.submitted_player_ids || [];
        setRoundSubmittedCount(ids.length);
        setRoundSubmittedPlayers(new Set(ids));
        setCurrentScreen(phase);
      } else if (phase === 'results') {
        setChainsData(data.chains || []);
        setResultsInitialChainIndex(data.results_chain_index ?? 0);
        setResultsInitialStepIndex(data.results_step_index ?? -1);
        setCurrentScreen('results');
      }
    }

    function onPlayerJoined(data) {
      setPlayers(prev => [...prev, { id: data.player_id, name: data.player_name, avatar: data.avatar, is_ai: data.is_ai || false }]);
      audio.playerJoin();
    }

    function onPlayerLeft(data) {
      // Keep the slot in the player list — the server keeps it for rejoin, and
      // the host needs to see the player to be able to kick them. Mark them as
      // disconnected so the UI can render the offline indicator.
      setDisconnectedPlayerIds(prev => {
        const next = new Set(prev);
        next.add(data.player_id);
        return next;
      });
    }

    function onPlayerRejoined(data) {
      setPlayers(prev => {
        const exists = prev.some(p => p.name === data.player_name);
        if (exists) {
          return prev.map(p => p.name === data.player_name
            ? { ...p, id: data.player_id, avatar: data.avatar }
            : p);
        }
        return [...prev, { id: data.player_id, name: data.player_name, avatar: data.avatar }];
      });
      // Clear stale offline/unresponsive markers keyed by either the new or the
      // pre-rejoin socket id (the latter is whatever player_left previously set).
      setDisconnectedPlayerIds(prev => {
        if (prev.size === 0) return prev;
        const next = new Set(prev);
        next.delete(data.player_id);
        return next;
      });
      setUnresponsivePlayerIds(prev => {
        if (prev.size === 0) return prev;
        const next = new Set(prev);
        next.delete(data.player_id);
        return next;
      });
    }

    function onPlayerUnresponsive(data) {
      setUnresponsivePlayerIds(prev => {
        const next = new Set(prev);
        next.add(data.player_id);
        return next;
      });
    }

    function onPlayerResponsive(data) {
      setUnresponsivePlayerIds(prev => {
        if (prev.size === 0) return prev;
        const next = new Set(prev);
        next.delete(data.player_id);
        return next;
      });
    }

    function onPlayerKicked(data) {
      setPlayers(prev => prev.filter(p => p.id !== data.player_id));
      setDisconnectedPlayerIds(prev => {
        if (prev.size === 0) return prev;
        const next = new Set(prev);
        next.delete(data.player_id);
        return next;
      });
      setUnresponsivePlayerIds(prev => {
        if (prev.size === 0) return prev;
        const next = new Set(prev);
        next.delete(data.player_id);
        return next;
      });
      setSubmittedPlayers(prev => {
        if (!prev.has(data.player_id)) return prev;
        const next = new Set(prev);
        next.delete(data.player_id);
        return next;
      });
      setRoundSubmittedPlayers(prev => {
        if (!prev.has(data.player_id)) return prev;
        const next = new Set(prev);
        next.delete(data.player_id);
        return next;
      });
    }

    function onAvatarChanged(data) {
      setPlayers(prev => prev.map(p =>
        p.id === data.player_id ? { ...p, avatar: data.avatar } : p
      ));
    }

    function onError(data) {
      console.error('[Pictaphone error]', data.message);
      if (resumePendingRef.current) {
        resumePendingRef.current = false;
        console.log('[Session] Saved session is no longer valid — clearing it');
        clearSession();
        return;
      }
      // Surface join/create failures (e.g. "Room not found") on the home screen.
      setJoinError(data.message || 'Something went wrong. Please try again.');
    }

    // Server rejected a prompt/guess (content moderation). Surface it inline in
    // the active phase so the player can edit and resubmit — the component
    // resets its optimistic "submitted" state when this becomes non-empty.
    function onSubmissionRejected(data) {
      setSubmissionError(data.message || "Let's keep it clean — please reword that.");
    }

    function onLobbyDoodleStroke(data) {
      if (data.stroke) setLobbyStrokes(prev => [...prev, data.stroke]);
    }

    function onGameStarted(data) {
      setLobbyStrokes([]);
      setSubmissionError('');
      pendingGameDataRef.current = data;
      setCurrentScreen('countdown');
    }

    function onPromptSubmitted(data) {
      setSubmittedCount(data.submitted_count);
      setSubmittedPlayers(prev => new Set([...prev, data.player_id]));
    }

    function onRoundStarted(data) {
      // Each round: either 'drawing' or 'guessing'
      clearDrawingDraft();
      setAlreadySubmitted(false);
      setSubmissionError('');
      const phase = data.phase;
      setCurrentRound(data.round);
      setTotalRounds(data.total_rounds);
      setRoundSubmittedCount(0);
      setRoundSubmittedPlayers(new Set());
      setRoundTimeLeft(data.timeout || 90);

      if (data.assignments && data.assignments[playerId]) {
        setAssignment(data.assignments[playerId]);
      }

      setCurrentScreen(phase); // 'drawing' or 'guessing'
    }

    function onRoundSubmission(data) {
      setRoundSubmittedCount(data.submitted_count);
      setRoundSubmittedPlayers(prev => new Set([...prev, data.player_id]));
    }

    function onGameComplete(data) {
      console.log('[App] Received game_complete:', {
        chainCount: data.chains?.length,
      });
      setChainsData(data.chains || []);
      setCurrentScreen('results');
    }

    function onResultsChainAdvanced(data) {
      setResultsChainIndex(data.chain_index);
    }

    function onResultsStepAdvanced(data) {
      setResultsStepSignal({ chainIndex: data.chain_index, stepIndex: data.step_index });
    }

    function onReactionReceived(data) {
      setReactionSignal({ ...data, _id: Date.now() + Math.random() });
    }

    function onGameEnded() {
      clearSession();
      setCurrentScreen('thanks');
    }

    function onPlayAgain(data) {
      console.log('[App] play_again received — returning to lobby');
      if (data?.players) setPlayers(data.players);
      if (data?.settings) setSettings(data.settings);
      setChainsData([]);
      setResultsChainIndex(-1);
      setResultsStepSignal(null);
      setResultsInitialChainIndex(null);
      setResultsInitialStepIndex(null);
      setReactionSignal(null);
      setAssignment(null);
      setSubmittedCount(0);
      setSubmittedPlayers(new Set());
      setRoundSubmittedCount(0);
      setRoundSubmittedPlayers(new Set());
      setAlreadySubmitted(false);
      setSubmittedPrompt('');
      setCurrentRound(0);
      setTotalRounds(0);
      setLobbyStrokes([]);
      setCurrentScreen('lobby');
    }

    function onSettingsUpdated(data) {
      if (data.settings) setSettings(data.settings);
    }

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connected', onConnected);
    socket.on('room_created', onRoomCreated);
    socket.on('room_joined', onRoomJoined);
    socket.on('room_rejoined', onRoomRejoined);
    socket.on('player_joined', onPlayerJoined);
    socket.on('player_left', onPlayerLeft);
    socket.on('player_rejoined', onPlayerRejoined);
    socket.on('player_unresponsive', onPlayerUnresponsive);
    socket.on('player_responsive', onPlayerResponsive);
    socket.on('player_kicked', onPlayerKicked);
    socket.on('avatar_changed', onAvatarChanged);
    socket.on('error', onError);
    socket.on('submission_rejected', onSubmissionRejected);
    socket.on('lobby_doodle_stroke', onLobbyDoodleStroke);
    socket.on('game_started', onGameStarted);
    socket.on('prompt_submitted', onPromptSubmitted);
    socket.on('round_started', onRoundStarted);
    socket.on('round_submission', onRoundSubmission);
    socket.on('game_complete', onGameComplete);
    socket.on('results_chain_advanced', onResultsChainAdvanced);
    socket.on('results_step_advanced', onResultsStepAdvanced);
    socket.on('reaction_received', onReactionReceived);
    socket.on('game_ended', onGameEnded);
    socket.on('play_again', onPlayAgain);
    socket.on('settings_updated', onSettingsUpdated);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connected', onConnected);
      socket.off('room_created', onRoomCreated);
      socket.off('room_joined', onRoomJoined);
      socket.off('room_rejoined', onRoomRejoined);
      socket.off('player_joined', onPlayerJoined);
      socket.off('player_left', onPlayerLeft);
      socket.off('player_rejoined', onPlayerRejoined);
      socket.off('player_unresponsive', onPlayerUnresponsive);
      socket.off('player_responsive', onPlayerResponsive);
      socket.off('player_kicked', onPlayerKicked);
      socket.off('avatar_changed', onAvatarChanged);
      socket.off('error', onError);
      socket.off('submission_rejected', onSubmissionRejected);
      socket.off('lobby_doodle_stroke', onLobbyDoodleStroke);
      socket.off('game_started', onGameStarted);
      socket.off('prompt_submitted', onPromptSubmitted);
      socket.off('round_started', onRoundStarted);
      socket.off('round_submission', onRoundSubmission);
      socket.off('game_complete', onGameComplete);
      socket.off('results_chain_advanced', onResultsChainAdvanced);
      socket.off('results_step_advanced', onResultsStepAdvanced);
      socket.off('reaction_received', onReactionReceived);
      socket.off('game_ended', onGameEnded);
      socket.off('play_again', onPlayAgain);
      socket.off('settings_updated', onSettingsUpdated);
    };
  }, [playerId]);

  const handleCreateRoom = (name) => {
    setJoinError('');
    socket.emit('create_room', { player_name: name });
  };

  const handleJoinRoom = (name, code) => {
    setJoinError('');
    socket.emit('join_room', { player_name: name, room_code: code });
  };

  const handleChangeAvatar = (avatar) => {
    socket.emit('change_avatar', { room_code: roomCode, avatar });
  };

  const handleStartGame = () => {
    socket.emit('start_game', { room_code: roomCode });
  };

  const handleCountdownComplete = () => {
    const data = pendingGameDataRef.current;
    if (!data) return;
    pendingGameDataRef.current = null;
    setSubmittedCount(0);
    setSubmittedPlayers(new Set());
    setRoundTimeLeft(data.timeout || 60);
    setAlreadySubmitted(false);
    setSubmittedPrompt('');
    if (data.settings) setSettings(data.settings);
    setPromptSuggestions(data.prompt_suggestions || []);
    setCurrentScreen('prompt_entry');
  };

  const handleAddAiPlayer = () => {
    socket.emit('add_ai_player', { room_code: roomCode });
  };

  const handleRemoveAiPlayer = (id) => {
    socket.emit('remove_ai_player', { room_code: roomCode, player_id: id });
  };

  const handleUpdateSettings = (newSettings) => {
    socket.emit('update_settings', { room_code: roomCode, settings: newSettings });
  };

  const handleLobbyDoodleStroke = (stroke) => {
    setLobbyStrokes(prev => [...prev, stroke]);
    socket.emit('lobby_doodle_stroke', { room_code: roomCode, stroke });
  };

  const handleSubmitPrompt = (prompt) => {
    socket.emit('submit_prompt', { room_code: roomCode, prompt });
  };

  const handleSubmitDrawing = (drawingData, thumbnailPng) => {
    console.log('[App] Sending drawing to server:', {
      roomCode,
      strokeCount: drawingData?.strokes?.length,
      totalDuration: drawingData?.totalDuration,
      canvasSize: drawingData?.canvas,
      dataKeys: Object.keys(drawingData || {}),
      thumbnailBytes: thumbnailPng?.byteLength ?? 0,
    });
    socket.emit('submit_drawing', {
      room_code: roomCode,
      drawing_data: drawingData,
      thumbnail_png: thumbnailPng || null,
    });
  };

  const handleSubmitGuess = (guess) => {
    socket.emit('submit_guess', { room_code: roomCode, guess });
  };

  const handleAdvanceResultsChain = (chainIndex) => {
    socket.emit('advance_results_chain', { room_code: roomCode, chain_index: chainIndex });
  };

  const handleSendReaction = (emoji) => {
    socket.emit('send_reaction', { room_code: roomCode, emoji });
  };

  const handleAdvanceResultsStep = (chainIndex, stepIndex) => {
    socket.emit('advance_results_step', { room_code: roomCode, chain_index: chainIndex, step_index: stepIndex });
  };

  const handleEndGame = () => {
    socket.emit('end_game', { room_code: roomCode });
  };

  // A single player flags an in-game drawing/guess as objectionable. The server
  // resolves the author + content from the live room (see handle_report_content).
  const handleReportContent = (itemId) => {
    if (!itemId) return;
    socket.emit('report_content', { room_code: roomCode, item_id: itemId });
  };

  // A single player leaves the reveal and returns home — the immediate escape
  // hatch from an uncomfortable game. Their slot stays server-side (harmless).
  const handleLeaveGame = () => {
    clearSession();
    setChainsData([]);
    setResultsChainIndex(-1);
    setResultsStepSignal(null);
    setResultsInitialChainIndex(null);
    setResultsInitialStepIndex(null);
    setReactionSignal(null);
    setRoomCode('');
    setCurrentScreen('home');
  };

  const handlePlayAgain = () => {
    socket.emit('play_again', { room_code: roomCode });
  };

  const handleKickPlayer = (kickPlayerId) => {
    socket.emit('kick_player', { room_code: roomCode, player_id: kickPlayerId });
  };

  // isHostOverride is set authoritatively from the server on create/join/rejoin.
  // Fallback to false — the fragile players[0] ordering can be corrupted by
  // player_left + player_rejoined cycles when the host briefly disconnects.
  const isHost = isHostOverride ?? false;

  const playersWithStatus = players.map(p => ({
    ...p,
    submitted: submittedPlayers.has(p.id),
    disconnected: disconnectedPlayerIds.has(p.id),
    unresponsive: unresponsivePlayerIds.has(p.id),
  }));

  const playersWithRoundStatus = players.map(p => ({
    ...p,
    submitted: roundSubmittedPlayers.has(p.id),
    disconnected: disconnectedPlayerIds.has(p.id),
    unresponsive: unresponsivePlayerIds.has(p.id),
  }));

  const playersWithPresence = players.map(p => ({
    ...p,
    disconnected: disconnectedPlayerIds.has(p.id),
    unresponsive: unresponsivePlayerIds.has(p.id),
  }));

  return (
    <div className="App">
      {!isConnected && (
        <div className="connection-dot disconnected">
          Offline
        </div>
      )}

      {currentScreen === 'home' && (
        <Home
          onCreateRoom={handleCreateRoom}
          onJoinRoom={handleJoinRoom}
          inviteRoomCode={inviteRoomCode}
          clearInviteRoomCode={() => setInviteRoomCode('')}
          error={joinError}
          clearError={() => setJoinError('')}
        />
      )}

      {currentScreen === 'lobby' && (
        <Lobby
          roomCode={roomCode}
          playerName={playerName}
          players={playersWithPresence}
          playerId={playerId}
          isHostOverride={isHost}
          onStartGame={handleStartGame}
          onChangeAvatar={handleChangeAvatar}
          onAddAiPlayer={handleAddAiPlayer}
          onRemoveAiPlayer={handleRemoveAiPlayer}
          onKickPlayer={handleKickPlayer}
          settings={settings}
          onUpdateSettings={handleUpdateSettings}
          lobbyStrokes={lobbyStrokes}
          onDoodleStroke={handleLobbyDoodleStroke}
        />
      )}

      {currentScreen === 'countdown' && (
        <GameCountdown onComplete={handleCountdownComplete} />
      )}

      {currentScreen === 'prompt_entry' && (
        <PromptEntry
          roomCode={roomCode}
          playerName={playerName}
          players={playersWithStatus}
          playerId={playerId}
          submittedCount={submittedCount}
          onSubmitPrompt={handleSubmitPrompt}
          timeLeft={roundTimeLeft}
          alreadySubmitted={alreadySubmitted}
          submittedPrompt={submittedPrompt}
          promptSuggestions={promptSuggestions}
          isHost={isHost}
          onKickPlayer={handleKickPlayer}
          submissionError={submissionError}
          onClearSubmissionError={() => setSubmissionError('')}
        />
      )}

      {currentScreen === 'drawing' && (
        <DrawingCanvas
          assignment={assignment}
          players={playersWithRoundStatus}
          playerId={playerId}
          submittedCount={roundSubmittedCount}
          onSubmitDrawing={handleSubmitDrawing}
          timeLeft={roundTimeLeft}
          currentRound={currentRound}
          totalRounds={totalRounds}
          alreadySubmitted={alreadySubmitted}
          isHost={isHost}
          onKickPlayer={handleKickPlayer}
        />
      )}

      {currentScreen === 'guessing' && (
        <GuessDrawing
          assignment={assignment}
          players={playersWithRoundStatus}
          playerId={playerId}
          submittedCount={roundSubmittedCount}
          onSubmitGuess={handleSubmitGuess}
          timeLeft={roundTimeLeft}
          currentRound={currentRound}
          totalRounds={totalRounds}
          alreadySubmitted={alreadySubmitted}
          isHost={isHost}
          onKickPlayer={handleKickPlayer}
          submissionError={submissionError}
          onClearSubmissionError={() => setSubmissionError('')}
        />
      )}

      {currentScreen === 'results' && (
        <Results
          chains={chainsData}
          isHost={isHost}
          playerId={playerId}
          playerName={playerName}
          onAdvanceChain={handleAdvanceResultsChain}
          forcedChainIndex={resultsChainIndex}
          onStepAdvanced={handleAdvanceResultsStep}
          forcedStep={resultsStepSignal}
          onEndGame={handleEndGame}
          onPlayAgain={handlePlayAgain}
          onReportContent={handleReportContent}
          onLeaveGame={handleLeaveGame}
          initialChainIndex={resultsInitialChainIndex}
          initialStepIndex={resultsInitialStepIndex}
          onSendReaction={handleSendReaction}
          reactionSignal={reactionSignal}
        />
      )}

      {currentScreen === 'thanks' && (
        <div className="thanks-container">
          <div className="thanks-card">
            <div className="thanks-icon"><span className="material-symbols-outlined" style={{fontSize: 'inherit'}}>celebration</span></div>
            <h1 className="thanks-title">Thanks for playing!</h1>
            <p className="thanks-subtitle">Hope you had a blast.</p>
            <button className="thanks-home-button" onClick={() => setCurrentScreen('home')}>
              Back to Home
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
